import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Box from '@material-ui/core/Box';

const useStyles = makeStyles((theme) => ({
    formControl: {
        marginBottom: 35,
    },
    formControlReadOnly: {
        marginBottom: 5,
    },
    label: {
        marginBottom: theme.spacing(2),
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: '1rem',
        fontWeight: 500,
        '&.Mui-error': {
            color: theme.palette.error.main,
        },
    },
    tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1, 1),
        marginTop: theme.spacing(1),
    },
    tag: {
        padding: theme.spacing(0.75, 1.2),
        border: '1px solid #d0d0d0',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        fontSize: '0.875rem',
        fontWeight: 400,
        transition: 'all 0.2s ease-in-out',
        userSelect: 'none',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1px',
            padding: theme.spacing(1, 2) - 1, // Compensate for thicker border
        },
    },
    tagReadOnly: {
        padding: theme.spacing(0.75, 1.2),
        border: '1px solid #d0d0d0',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
    },
    tagSelected: {
        borderColor: theme.palette.primary.main,
        borderWidth: '1px',
        padding: theme.spacing(1, 2) - 1, // Compensate for thicker border
        backgroundColor: '#f0f7ff',
    },
    tagError: {
        borderColor: theme.palette.error.main,
    },
    helperText: {
        marginTop: theme.spacing(1),
    },
}));

const TagSelect = ({
    id,
    label,
    options,
    value = [],
    onChange,
    required = false,
    error = false,
    helperText = '',
    readOnly = false,
}) => {
    const classes = useStyles();

    const handleTagClick = (option) => {
        if (readOnly) return;

        const isSelected = value.includes(option);
        const newValue = isSelected ? value.filter((item) => item !== option) : [...value, option];

        // Simulate event structure for compatibility with existing onChange handler
        const simulatedEvent = {
            target: {
                name: id,
                value: newValue,
            },
        };
        onChange(simulatedEvent);
    };

    const isSelected = (option) => value.includes(option);

    // When read-only, only show selected items
    const displayOptions = readOnly ? options.filter((opt) => value.includes(opt)) : options;

    return (
        <FormControl className={`${classes.formControl} ${readOnly ? classes.formControlReadOnly : ''}`} error={error}>
            {label && (
                <FormLabel className={classes.label} required={required} error={error}>
                    {label}
                </FormLabel>
            )}
            <Box className={classes.tagsContainer}>
                {displayOptions.map((option) => (
                    <Box
                        key={option}
                        className={`${readOnly ? classes.tagReadOnly : classes.tag} ${
                            isSelected(option) && !readOnly ? classes.tagSelected : ''
                        } ${error ? classes.tagError : ''}`}
                        onClick={() => handleTagClick(option)}
                        role={readOnly ? 'presentation' : 'button'}
                        tabIndex={readOnly ? -1 : 0}
                        onKeyPress={
                            readOnly
                                ? undefined
                                : (e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          handleTagClick(option);
                                      }
                                  }
                        }
                        aria-pressed={readOnly ? undefined : isSelected(option)}
                    >
                        {option}
                    </Box>
                ))}
            </Box>
            {helperText && (
                <FormHelperText className={classes.helperText} error={error}>
                    {helperText}
                </FormHelperText>
            )}
        </FormControl>
    );
};

export default TagSelect;
